import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box, Container, Paper, Title, Text, Group, Stack, Grid,
  TextInput, NumberInput, Textarea, Badge, ActionIcon, Button,
  Table, SegmentedControl, Tooltip, Switch, Popover, Checkbox, Modal,
} from '@mantine/core';
import {
  IconSearch, IconDownload, IconPrinter, IconCopy,
  IconTrash, IconCopyPlus, IconX, IconTemplate, IconColumns, IconPlus,
} from '@tabler/icons-react';
import PlatformNavBar from '../../components/PlatformNavBar';
import Footer from '../../components/Footer';

// ─── Column definitions ───────────────────────────────────────────────────────
const COLS = [
  { key: 'product',  label: 'Product',      sub: null,     align: 'left',  always: true },
  { key: 'billing',  label: 'Frequency',     sub: null,     align: 'center' },
  { key: 'volume',   label: 'Volume',        sub: null,     align: 'right' },
  { key: 'list',     label: 'Cust list',     sub: '$/unit', align: 'right' },
  { key: 'disc',     label: 'Disc %',        sub: null,     align: 'right' },
  { key: 'sell',     label: 'Cust sell',     sub: '$/unit', align: 'right' },
  { key: 'ext',      label: 'Cust ext',      sub: 'MRC',    align: 'right' },
  { key: 'cost',     label: 'Presidio cost', sub: '$/unit', align: 'right', internal: true },
  { key: 'adjCost',  label: 'Adj cost',      sub: '$/unit', align: 'right', internal: true },
  { key: 'extCost',  label: 'Presidio ext',  sub: 'cost',   align: 'right', internal: true },
  { key: 'margin',   label: 'Margin',        sub: null,     align: 'right', internal: true },
  { key: 'actions',  label: '',              sub: null,     align: 'right', always: true },
];

// ─── Catalog ─────────────────────────────────────────────────────────────────
// Static export of the Presidio Default Pricing Table.
// To swap in a live source, replace the body of loadCatalog() with a fetch
// that returns the same shape: [{ category, name, sell, cost }, ...]
const CATALOG = [{"category":"FLEX","name":"Flex - Named Users","sell":150.0,"cost":90.0},{"category":"CONNECTIVITY - FLEX","name":"PSTN Connectivity - UNITED STATES & CANADA Toll Free Inbound Minute","sell":0.018,"cost":0.01799676},{"category":"CONNECTIVITY - FLEX","name":"PSTN Connectivity - UNITED STATES Local Incoming Minutes","sell":0.0045,"cost":0.00234},{"category":"CONNECTIVITY - FLEX","name":"PSTN Connectivity - PUERTO RICO Local Incoming Minutes","sell":0.01,"cost":0.0099994},{"category":"CONNECTIVITY - FLEX","name":"PSTN Connectivity - Programmable Outbound Minute - United States & Canada","sell":0.01,"cost":0.005200000000000001},{"category":"CONNECTIVITY - FLEX","name":"PSTN Connectivity - Programmable Outbound Minute - United States - Alaska","sell":0.0905,"cost":0.0905},{"category":"CONNECTIVITY - FLEX","name":"PSTN Connectivity - Programmable Outbound Minute - United States - Hawaii","sell":0.01,"cost":0.009000000000000001},{"category":"CONNECTIVITY - FLEX","name":"PSTN Connect - Prog Outbound Minute - United States & Canada - Toll Free","sell":0.009,"cost":0.008999189999999999},{"category":"CONNECTIVITY - FLEX","name":"PSTN Connectivity - Programmable Outbound Minute - Puerto Rico","sell":0.02,"cost":0.010400000000000001},{"category":"CONNECTIVITY - FLEX","name":"PSTN Connectivity - GERMANY Local Incoming Minutes","sell":0.01,"cost":0.005200000000000001},{"category":"CONNECTIVITY - FLEX","name":"PSTN Connectivity - GERMANY Mobile Incoming Minutes","sell":0.01,"cost":0.005200000000000001},{"category":"CONNECTIVITY - FLEX","name":"PSTN Connectivity - GERMANY Toll Free Incoming Minutes","sell":0.01,"cost":0.005200000000000001},{"category":"CONNECTIVITY - FLEX","name":"PSTN Connectivity - GERMANY Toll Free Local Incoming Minutes","sell":0.01,"cost":0.004677},{"category":"CONNECTIVITY - FLEX","name":"PSTN Connectivity - Programmable Outbound Minute - Germany","sell":0.012,"cost":0.005122799999999999},{"category":"CONNECTIVITY - FLEX","name":"PSTN Connectivity - Programmable Outbound Minute - Germany - Mobile","sell":0.346,"cost":0.17992},{"category":"CONNECTIVITY - FLEX","name":"PSTN Connectivity - Programmable Outbound Minute - Germany - Mobile - Eplus","sell":0.346,"cost":0.17992},{"category":"CONNECTIVITY - FLEX","name":"PSTN Connectivity - Programmable Outbound Minute - Germany - Mobile - Other","sell":0.234,"cost":0.12168000000000001},{"category":"CONNECTIVITY - FLEX","name":"PSTN Connectivity - Programmable Outbound Minute - Germany - Special Services","sell":0.0118,"cost":0.0118},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"Brand Registration Fee","sell":5.0,"cost":3.0},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"Campaign Registration Fee - Standard","sell":12.5,"cost":7.5},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Inbound SMS - AT&T","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Inbound SMS - Other","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Inbound SMS - Sprint","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Inbound SMS - T-Mobile","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Inbound SMS - US Cellular","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Inbound SMS - Verizon","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Toll Free Inbound SMS - AT&T","sell":0.0083,"cost":0.00415},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Toll Free Inbound SMS - Other","sell":0.0083,"cost":0.00415},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Toll Free Inbound SMS - Sprint","sell":0.0083,"cost":0.00415},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Toll Free Inbound SMS - T-Mobile","sell":0.0083,"cost":0.00415},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Toll Free Inbound SMS - US Cellular","sell":0.0083,"cost":0.00415},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Toll Free Inbound SMS - Verizon","sell":0.0083,"cost":0.00415},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Inbound Shortcode SMS - AT&T","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Inbound Shortcode SMS - Other","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Inbound Shortcode SMS - Sprint","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Inbound Shortcode SMS - T-Mobile","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Inbound Shortcode SMS - US Cellular","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Inbound Shortcode SMS - Verizon","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Outbound SMS - AT&T","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Outbound SMS - Other","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Outbound SMS - Sprint","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Outbound SMS - T-Mobile","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Outbound SMS - US Cellular","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Outbound SMS - Verizon","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Toll Free Outbound SMS - AT&T","sell":0.0083,"cost":0.00415},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Toll Free Outbound SMS - Other","sell":0.0083,"cost":0.00415},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Toll Free Outbound SMS - Sprint","sell":0.0083,"cost":0.00415},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Toll Free Outbound SMS - T-Mobile","sell":0.0083,"cost":0.00415},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Toll Free Outbound SMS - US Cellular","sell":0.0083,"cost":0.00415},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Toll Free Outbound SMS - Verizon","sell":0.0083,"cost":0.00415},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Outbound Shortcode SMS - AT&T","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Outbound Shortcode SMS - Other","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Outbound Shortcode SMS - Sprint","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Outbound Shortcode SMS - T-Mobile","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Outbound Shortcode SMS - US Cellular","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Outbound Shortcode SMS - Verizon","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Inbound MMS - AT&T","sell":0.0083,"cost":0.002075},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Inbound MMS - Other","sell":0.01,"cost":0.01},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Inbound MMS - Sprint","sell":0.01,"cost":0.01},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Inbound MMS - T-Mobile","sell":0.01,"cost":0.01},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Inbound MMS - US Cellular","sell":0.01,"cost":0.01},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"UNITED STATES Inbound MMS - Verizon","sell":0.01,"cost":0.01},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"ARGENTINA Outbound SMS - Claro","sell":0.0935,"cost":0.07106},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"ARGENTINA Outbound SMS - movistar","sell":0.0935,"cost":0.07106},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"ARGENTINA Outbound SMS - NEXTEL","sell":0.0935,"cost":0.07106},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"ARGENTINA Outbound SMS - Other","sell":0.0935,"cost":0.07106},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"ARGENTINA Outbound SMS - Personal","sell":0.0935,"cost":0.07106},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"BARBADOS Outbound SMS - Digicel","sell":0.2624,"cost":0.12558464000000003},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"BARBADOS Outbound SMS - LIME","sell":0.2624,"cost":0.12558464000000003},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"BARBADOS Outbound SMS - Other","sell":0.2624,"cost":0.12558464000000003},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"BRAZIL Outbound SMS - Claro","sell":0.0599,"cost":0.00198},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"BRAZIL Outbound SMS - CTBC","sell":0.0599,"cost":0.00198},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"BRAZIL Outbound SMS - NEXTEL","sell":0.0599,"cost":0.00198},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"BRAZIL Outbound SMS - Oi","sell":0.0599,"cost":0.00198},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"BRAZIL Outbound SMS - Other","sell":0.0599,"cost":0.00198},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"BRAZIL Outbound SMS - Sercomtel","sell":0.0599,"cost":0.015010940000000004},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"BRAZIL Outbound SMS - Surf Telecom","sell":0.0599,"cost":0.015010940000000004},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"BRAZIL Outbound SMS - TIM","sell":0.0599,"cost":0.015010940000000004},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"BRAZIL Outbound SMS - Vivo","sell":0.0599,"cost":0.015010940000000004},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Local Inbound SMS - Bell & Virgin","sell":0.0079,"cost":0.0019797400000000007},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Local Inbound SMS - Freedom Mobile","sell":0.0079,"cost":0.0019797400000000007},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Local Inbound SMS - Other","sell":0.0079,"cost":0.0019797400000000007},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Local Inbound SMS - Rogers","sell":0.0079,"cost":0.0019797400000000007},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Local Inbound SMS - Telus","sell":0.0079,"cost":0.0019797400000000007},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Local Inbound SMS - Videotron","sell":0.0079,"cost":0.0019797400000000007},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Local Outbound SMS - Telus","sell":0.0079,"cost":0.0019797400000000007},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Local Outbound SMS - Bell & Virgin","sell":0.0079,"cost":0.0019797400000000007},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Local Outbound SMS - Freedom Mobile","sell":0.0079,"cost":0.0019797400000000007},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Local Outbound SMS - Other","sell":0.0079,"cost":0.0019797400000000007},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Local Outbound SMS - Rogers","sell":0.0079,"cost":0.0019797400000000007},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Local Outbound SMS - Videotron","sell":0.0079,"cost":0.0019797400000000007},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Toll Free Inbound SMS - Bell & Virgin","sell":0.0079,"cost":0.00395},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Toll Free Inbound SMS - Other","sell":0.0079,"cost":0.00395},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Toll Free Inbound SMS - Rogers","sell":0.0079,"cost":0.00395},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Toll Free Inbound SMS - Telus","sell":0.0079,"cost":0.00395},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Toll Free Inbound SMS - Freedom Mobile","sell":0.0079,"cost":0.00395},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Toll Free Inbound SMS - Videotron","sell":0.0079,"cost":0.00395},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Toll Free Outbound SMS - Bell & Virgin","sell":0.0079,"cost":0.00395},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Toll Free Outbound SMS - Other","sell":0.0079,"cost":0.00395},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Toll Free Outbound SMS - Rogers","sell":0.0079,"cost":0.00395},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Toll Free Outbound SMS - Telus","sell":0.0079,"cost":0.00395},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Toll Free Outbound SMS - Freedom Mobile","sell":0.0079,"cost":0.00395},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CANADA Toll Free Outbound SMS - Videotron","sell":0.0079,"cost":0.00395},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CAYMAN ISLANDS Outbound SMS - Digicel","sell":0.2476,"cost":0.2476},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CAYMAN ISLANDS Outbound SMS - LIME","sell":0.2476,"cost":0.2476},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"CAYMAN ISLANDS Outbound SMS - Other","sell":0.2476,"cost":0.2476},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"COSTA RICA Outbound SMS - Claro","sell":0.042,"cost":0.021840000000000002},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"COSTA RICA Outbound SMS - Kolbi","sell":0.042,"cost":0.021840000000000002},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"COSTA RICA Outbound SMS - movistar","sell":0.042,"cost":0.021840000000000002},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"COSTA RICA Outbound SMS - Other","sell":0.042,"cost":0.021840000000000002},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"COSTA RICA Outbound SMS - Virtualis","sell":0.042,"cost":0.021840000000000002},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"DENMARK Outbound SMS - 3","sell":0.0524,"cost":0.046112},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"DENMARK Outbound SMS - Lycamobile","sell":0.0524,"cost":0.046112},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"DENMARK Outbound SMS - MACH Connectivity","sell":0.0524,"cost":0.046112},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"DENMARK Outbound SMS - Other","sell":0.0524,"cost":0.046112},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"DENMARK Outbound SMS - TDC","sell":0.0524,"cost":0.046112},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"DENMARK Outbound SMS - Telenor","sell":0.0524,"cost":0.046112},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"DENMARK Outbound SMS - Telia","sell":0.0524,"cost":0.046112},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"DENMARK Outbound SMS - Vectone Mobile (Mundio Mobile)","sell":0.0524,"cost":0.046112},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"DOMINICAN REPUBLIC Outbound SMS - Claro","sell":0.1014,"cost":0.05898438},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"DOMINICAN REPUBLIC Outbound SMS - Orange","sell":0.1014,"cost":0.05898438},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"DOMINICAN REPUBLIC Outbound SMS - Other","sell":0.1014,"cost":0.05898438},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"DOMINICAN REPUBLIC Outbound SMS - Tricom","sell":0.1014,"cost":0.05989698},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"DOMINICAN REPUBLIC Outbound SMS - Viva","sell":0.1014,"cost":0.05898438},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"GRENADA Outbound SMS - LIME","sell":0.2462,"cost":0.16229504},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"GRENADA Outbound SMS - Other","sell":0.2462,"cost":0.16229504},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"GUAM & NORTHERN MARIANA ISLANDS Outbound SMS - Docomo Pacific","sell":0.0687,"cost":0.03571713},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"GUAM & NORTHERN MARIANA ISLANDS Outbound SMS - IT&E","sell":0.0687,"cost":0.03571713},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"GUAM & NORTHERN MARIANA ISLANDS Outbound SMS - MPULSE","sell":0.0687,"cost":0.03571713},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"INDIA Outbound SMS - Aircel","sell":0.0832,"cost":0.08315007999999999},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"INDIA Outbound SMS - Airtel","sell":0.0832,"cost":0.08315007999999999},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"INDIA Outbound SMS - BSNL","sell":0.0832,"cost":0.08315007999999999},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"INDIA Outbound SMS - Etisalat DB Telecom","sell":0.0832,"cost":0.08315007999999999},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"INDIA Outbound SMS - Idea Cellular","sell":0.0832,"cost":0.08315007999999999},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"INDIA Outbound SMS - Loop Mobile","sell":0.0832,"cost":0.08315007999999999},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"INDIA Outbound SMS - MTNL","sell":0.0832,"cost":0.08315007999999999},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"INDIA Outbound SMS - MTS","sell":0.0832,"cost":0.08315007999999999},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"INDIA Outbound SMS - Other","sell":0.0832,"cost":0.08315007999999999},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"INDIA Outbound SMS - Ping Mobile","sell":0.0832,"cost":0.08315007999999999},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"INDIA Outbound SMS - Reliance","sell":0.0832,"cost":0.08315007999999999},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"INDIA Outbound SMS - S Tel","sell":0.0832,"cost":0.08315007999999999},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"INDIA Outbound SMS - Tata DoCoMo","sell":0.0832,"cost":0.08315007999999999},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"INDIA Outbound SMS - Tata indicom","sell":0.0832,"cost":0.08315007999999999},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"INDIA Outbound SMS - Uninor","sell":0.0832,"cost":0.08315007999999999},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"INDIA Outbound SMS - Videocon","sell":0.0832,"cost":0.08315007999999999},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"INDIA Outbound SMS - Vodafone India","sell":0.0832,"cost":0.08315007999999999},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"JAMAICA Outbound SMS - Digicel","sell":0.2819,"cost":0.2819},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"JAMAICA Outbound SMS - LIME","sell":0.2819,"cost":0.2819},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"JAMAICA Outbound SMS - Other","sell":0.2819,"cost":0.2819},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"MEXICO Outbound SMS - AT&T","sell":0.16371,"cost":0.060785522999999994},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"MEXICO Outbound SMS - movistar","sell":0.16371,"cost":0.060785522999999994},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"MEXICO Outbound SMS - NEXTEL","sell":0.16371,"cost":0.060785522999999994},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"MEXICO Outbound SMS - Other","sell":0.16371,"cost":0.060785522999999994},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"MEXICO Outbound SMS - Telcel","sell":0.16371,"cost":0.060785522999999994},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"PHILIPPINES Outbound SMS - Globe Telecom","sell":0.2001,"cost":0.16990491},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"PHILIPPINES Outbound SMS - Other","sell":0.2001,"cost":0.16990491},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"PHILIPPINES Outbound SMS - Smart Communications","sell":0.2001,"cost":0.16990491},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"PHILIPPINES Outbound SMS - Sun Cellular","sell":0.2001,"cost":0.16990491},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"PUERTO RICO Outbound SMS - Other","sell":0.042,"cost":0.042},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"PUERTO RICO Outbound SMS - PR Wireless","sell":0.042,"cost":0.042},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"TRINIDAD AND TOBAGO Outbound SMS - bMobile","sell":0.2992,"cost":0.2992},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"TRINIDAD AND TOBAGO Outbound SMS - Digicel","sell":0.2992,"cost":0.2992},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"TRINIDAD AND TOBAGO Outbound SMS - Other","sell":0.2992,"cost":0.2992},{"category":"CONNECTIVITY - PROGRAMMABLE MESSAGING","name":"US VIRGIN ISLANDS Outbound SMS","sell":0.02,"cost":0.0104},{"category":"CONNECTIVITY - PROGRAMMABLE VOICE","name":"UNITED STATES & CANADA Toll Free inbound Minute","sell":0.022,"cost":0.0132},{"category":"CONNECTIVITY - PROGRAMMABLE VOICE","name":"UNITED STATES Local Incoming Minute","sell":0.0085,"cost":0.00442},{"category":"CONNECTIVITY - PROGRAMMABLE VOICE","name":"Programmable Outbound Minute - United States - Alaska","sell":0.0945,"cost":0.0945},{"category":"CONNECTIVITY - PROGRAMMABLE VOICE","name":"Programmable Outbound Minute - United States - Hawaii","sell":0.014,"cost":0.00728},{"category":"CONNECTIVITY - PROGRAMMABLE VOICE","name":"Programmable Outbound Minute - United States & Canada","sell":0.014,"cost":0.00728},{"category":"CONNECTIVITY - PROGRAMMABLE VOICE","name":"Programmable Outbound Minute - United States & Canada - Toll Free","sell":0.014,"cost":0.00728},{"category":"CONNECTIVITY - PROGRAMMABLE VOICE","name":"Programmable Outbound Minute - India","sell":0.0473,"cost":0.035948},{"category":"CONNECTIVITY - PROGRAMMABLE VOICE","name":"Programmable Outbound Minute - India - Major Cities","sell":0.0473,"cost":0.035948},{"category":"CONNECTIVITY - PROGRAMMABLE VOICE","name":"Programmable Outbound Minute - India - Mobile","sell":0.0368,"cost":0.0104},{"category":"CONNECTIVITY - PROGRAMMABLE VOICE","name":"SIP Interface - SIP In Calls","sell":0.004,"cost":0.0027},{"category":"CONNECTIVITY - PROGRAMMABLE VOICE","name":"SIP Interface - SIP Out Calls","sell":0.004,"cost":0.0027},{"category":"CONNECTIVITY - PROGRAMMABLE VOICE","name":"SIP Call Transfer (SIP REFER from Twilio)","sell":0.1,"cost":0.1},{"category":"CONNECTIVITY - PROGRAMMABLE VOICE","name":"Fast Transcription Minute","sell":0.05,"cost":0.0225},{"category":"CONNECTIVITY - PROGRAMMABLE VOICE","name":"Recording Storage","sell":0.0005,"cost":0.00015000000000000001},{"category":"CONNECTIVITY - LOOKUP","name":"INTERNATIONAL Carrier Lookup - API","sell":0.005,"cost":0.00375},{"category":"CONNECTIVITY - LOOKUP","name":"US/CA Caller Name Lookup - API","sell":0.01,"cost":0.008},{"category":"CONNECTIVITY - SIP TRUNKING","name":"Trunking Local Inbound Minute - Canada","sell":0.0045,"cost":0.00234},{"category":"CONNECTIVITY - SIP TRUNKING","name":"Trunking Local Inbound Minute - United States","sell":0.0034,"cost":0.001768},{"category":"CONNECTIVITY - SIP TRUNKING","name":"Trunking Outbound Minute - Canada","sell":0.0095,"cost":0.00494},{"category":"CONNECTIVITY - SIP TRUNKING","name":"Trunking Outbound Minute - United States - 48 States (Zone 1)","sell":0.01,"cost":0.005200000000000001},{"category":"CONNECTIVITY - SIP TRUNKING","name":"Trunking Outbound Minute - United States - Hawaii (Zone 2)","sell":0.01,"cost":0.005200000000000001},{"category":"CONNECTIVITY - SIP TRUNKING","name":"Trunking Outbound Minute - United States - Alaska (Zone 3)","sell":0.0862,"cost":0.044824},{"category":"CONNECTIVITY - SIP TRUNKING","name":"Trunking Outbound Minute - United States - High Cost (Zone 4)","sell":0.062,"cost":0.03224},{"category":"CONNECTIVITY - SIP TRUNKING","name":"CANADA Emergency Calling Number - Trunking","sell":0.75,"cost":0.5625},{"category":"CONNECTIVITY - SIP TRUNKING","name":"UNITED STATES Emergency Calling Number - Trunking","sell":0.75,"cost":0.5625},{"category":"PHONE NUMBERS AND OTHER","name":"UNITED STATES & CANADA Toll Free Phone Number","sell":2.15,"cost":0.4299999999999999},{"category":"PHONE NUMBERS AND OTHER","name":"UNITED STATES Local Phone Number","sell":1.15,"cost":0.25001},{"category":"PHONE NUMBERS AND OTHER","name":"US/CA Caller Name Lookup - Programmable Voice","sell":0.01,"cost":0.006},{"category":"PHONE NUMBERS AND OTHER","name":"US/CA Caller Name Lookup - Trunking","sell":0.01,"cost":0.005},{"category":"PHONE NUMBERS AND OTHER","name":"Studio - Per Execution","sell":0.0025,"cost":0.0025},{"category":"PHONE NUMBERS AND OTHER","name":"Function Invocation","sell":0.0001,"cost":4e-05},{"category":"PHONE NUMBERS AND OTHER","name":"Gather Speech Recognition","sell":0.0025,"cost":0.0025},{"category":"PHONE NUMBERS AND OTHER","name":"Text To Speech - Amazon Polly","sell":0.0008,"cost":0.0008},{"category":"PHONE NUMBERS AND OTHER","name":"TaskRouter","sell":0.06,"cost":0.06},{"category":"SUPPORT PLANS","name":"Twilio-Delivered Developer Support (DEFAULT - $0)","sell":0.0,"cost":0.0},{"category":"SUPPORT PLANS","name":"Twilio-Delivered Production Support (4% of Monthly Spend or $250 minimum)","sell":250.0,"cost":250.0},{"category":"SUPPORT PLANS","name":"Twilio-Delivered Business Support (6% of Monthly Spend of $1500 minimum)","sell":1500.0,"cost":1500.0},{"category":"SUPPORT PLANS","name":"Twilio-Delivered Personalized Support (8% of Monthly Spend or $4250 minimum)","sell":5000.0,"cost":5000.0},{"category":"MANUALLY ADDED SKUS","name":"Twilio Security Edition % Spend - 10% or $5625 minimum","sell":5625.0,"cost":5625.0},{"category":"MANUALLY ADDED SKUS","name":"Twilio-Leased Random Short Code","sell":12000.0,"cost":12000.0},{"category":"MANUALLY ADDED SKUS","name":"Short Code Setup Fee","sell":1000.0,"cost":1000.0},{"category":"MANUALLY ADDED SKUS","name":"Verify Pro Packaged Plan - 100K","sell":2975.0,"cost":2618.0},{"category":"MANUALLY ADDED SKUS","name":"Verify Pro Packaged Plan - 100K - Overages","sell":0.0298,"cost":0.02622}];

// ─── Default billing frequency overrides ──────────────────────────────────────
// Most catalog products default to 'monthly'. A handful of SKUs are billed
// differently in practice (e.g. Twilio's Brand Registration is a one-time fee,
// not a recurring monthly charge) — list those overrides here by exact product
// name. Looked up whenever a line is added via search, template, or companion rule.
const DEFAULT_BILLING = {
  'Brand Registration Fee': 'onetime',
};
const defaultBillingFor = name => DEFAULT_BILLING[name] || 'monthly';

// ─── Auto-companion rules ─────────────────────────────────────────────────────
// When any non-companion line satisfies trigger(), the named companion SKUs are
// auto-inserted as editable line items. They are removed if the trigger clears.
// Push additional objects here to add more rules.
const AUTO_COMPANION_RULES = [
  {
    trigger: line =>
      line.category === 'CONNECTIVITY - PROGRAMMABLE MESSAGING' &&
      line.name.toUpperCase().includes('SMS') &&
      line.name !== 'Brand Registration Fee' &&
      line.name !== 'Campaign Registration Fee - Standard',
    companions: ['Brand Registration Fee', 'Campaign Registration Fee - Standard'],
  },
];

// ─── Templates ───────────────────────────────────────────────────────────────
// Each template adds a pre-defined set of products in one click.
// Add new entries here; companion rules run automatically after template apply.
const TEMPLATES = [
  {
    name: 'Template — SMS Messaging',
    description: 'Local phone number + inbound & outbound US SMS',
    items: [
      { name: 'UNITED STATES Local Phone Number', volume: 1 },
      { name: 'UNITED STATES Inbound SMS - Other', volume: 1000 },
      { name: 'UNITED STATES Outbound SMS - Other', volume: 1000 },
    ],
  },
];

const LS_KEY = 'twilio_quote_builder_v2';
const DEFAULT_NOTES =
  'This quote represents an estimation of one-time and monthly recurring spend, based on estimated usage. Billing will be monthly, based on actual usage amounts from the platform.';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtMoney(v, unitPrice = false) {
  if (v == null || isNaN(v)) return '—';
  if (unitPrice) {
    if (v === 0) return '$0.00';
    if (Math.abs(v) < 1) {
      let s = v.toFixed(5).replace(/0+$/, '');
      if (s.endsWith('.')) s += '00';
      if ((s.split('.')[1] || '').length < 2) s = v.toFixed(2);
      return '$' + s;
    }
  }
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function marginHex(m) {
  return m >= 0.4 ? '#0E7C5A' : m >= 0.2 ? '#B7791F' : '#C0392B';
}

function marginMantineColor(m) {
  return m >= 0.4 ? 'green' : m >= 0.2 ? 'yellow' : 'red';
}

// Returns the effective customer sell price, overriding per-row pricing when
// a standard discount is active.
function getEffectiveSell(l, stdDisc) {
  return stdDisc.enabled
    ? Math.max(0, +(l.list * (1 - stdDisc.pct / 100)).toFixed(6))
    : (+l.sell || 0);
}

// Format a cost/sell value for display: min 2 decimals, up to 6 (no trailing zeros beyond 2)
function fmtCost(v) {
  if (v == null || isNaN(v)) return '—';
  const s = (+v).toFixed(6).replace(/0+$/, '');
  const dec = (s.split('.')[1] || '').length;
  return '$' + (dec < 2 ? (+v).toFixed(2) : s);
}

// String value for a sell input: at least 2 decimal places
function fmtSellInput(v) {
  const n = +v || 0;
  const dec = (n.toString().split('.')[1] || '').length;
  return dec < 2 ? n.toFixed(2) : n.toString();
}

// String value for a cost input: up to 6 decimal places, min 2
function fmtCostInput(v) {
  const n = +v || 0;
  const s = n.toFixed(6).replace(/0+$/, '');
  const dec = (s.split('.')[1] || '').length;
  return dec < 2 ? n.toFixed(2) : s;
}

// Inline style for table cell inputs — hover/focus borders handled via onFocus/onBlur
const cellInput = {
  textAlign: 'right',
  border: '1px solid transparent',
  borderRadius: 6,
  padding: '4px 6px',
  fontSize: 13,
  background: 'transparent',
  outline: 0,
  color: 'inherit',
  fontFamily: 'inherit',
  fontVariantNumeric: 'tabular-nums',
  transition: 'border-color 0.1s',
  MozAppearance: 'textfield',
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function TwilioCalculatorApp({ onBackToPlatformSelector, onBackToHome }) {
  const [view, setView] = useState('internal');
  const [meta, setMeta] = useState({ cust: '', sid: '', so: '' });
  const [lines, setLines] = useState([]);
  const [autoFees, setAutoFees] = useState(true);
  const [carrierRates, setCarrierRates] = useState({ sms: 0.005, mms: 0.01 });
  const [carrierBilling, setCarrierBilling] = useState({ sms: 'monthly', mms: 'monthly' });
  const [notesVal, setNotesVal] = useState(DEFAULT_NOTES);
  const [myo, setMyo] = useState({ sku: 'TWILIO-SAAS', term: 12 });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [restored, setRestored] = useState(false);
  const [stdDisc, setStdDisc] = useState({ enabled: false, pct: 0 });
  const [hiddenCols, setHiddenCols] = useState(new Set());
  const [dismissedCompanions, setDismissedCompanions] = useState(new Set());
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [customForm, setCustomForm] = useState({ name: '', category: 'CUSTOM / MANUAL', volume: 1, list: 0, sell: 0, cost: 0, billing: 'monthly' });

  const nextId = useRef(1);
  const searchContainerRef = useRef(null);
  const searchInputRef = useRef(null);

  // ─── Derived values ──────────────────────────────────────────────────────────
  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    const terms = q.split(/\s+/);
    return CATALOG.filter(p => {
      const n = p.name.toLowerCase();
      return terms.every(t => n.includes(t));
    }).slice(0, 60);
  }, [searchQuery]);

  const groupedResults = useMemo(() => {
    const groups = [];
    let lastCat = null;
    searchResults.forEach(p => {
      if (p.category !== lastCat) {
        groups.push({ category: p.category, items: [] });
        lastCat = p.category;
      }
      groups[groups.length - 1].items.push(p);
    });
    return groups;
  }, [searchResults]);

  const templateResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return TEMPLATES;
    return TEMPLATES.filter(t => t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q));
  }, [searchQuery]);

  const autoRows = useMemo(() => {
    if (!autoFees) return [];
    let smsVol = 0, mmsVol = 0;
    lines.forEach(l => {
      const n = l.name.toUpperCase();
      const v = +l.volume || 0;
      if (n.includes('SMS') || n.includes('RCS')) smsVol += v;
      if (n.includes('MMS')) mmsVol += v;
    });
    const out = [];
    if (smsVol > 0) out.push({ auto: true, key: 'sms', name: 'US SMS / RCS carrier fees (estimate)', volume: smsVol, sell: carrierRates.sms, cost: carrierRates.sms, billing: carrierBilling.sms || 'monthly' });
    if (mmsVol > 0) out.push({ auto: true, key: 'mms', name: 'US MMS / RCS media carrier fees (estimate)', volume: mmsVol, sell: carrierRates.mms, cost: carrierRates.mms, billing: carrierBilling.mms || 'monthly' });
    return out;
  }, [lines, autoFees, carrierRates, carrierBilling]);

  // Grand total breaks lines out by billing frequency, then extends the monthly-
  // and annual-billed buckets across the selected contract term so One-Time, MRR,
  // ARR, Total, and Total Cost all move together when Term for Extended Price
  // changes. One-time fees are paid once and intentionally don't scale with term.
  // custTotal/costTotal stay as the plain monthly RATE (not term-extended) since
  // myOrdersSegments() uses them for the "/mo" unit price line and extends them
  // itself for the MyOrders "Customer Extended Price" text.
  const { custTotal, costTotal, otCust, mrrCust, arrCust, grandCust, grandCost, blendMargin } = useMemo(() => {
    const term = Math.max(1, +myo.term || 12);
    let otC = 0, otK = 0, moC = 0, moK = 0, anC = 0, anK = 0;
    lines.forEach(l => {
      const ext = (+l.volume || 0) * getEffectiveSell(l, stdDisc);
      const extCost = (+l.volume || 0) * (+l.adjCost || 0);
      const billing = l.billing || 'monthly';
      if (billing === 'onetime') { otC += ext; otK += extCost; }
      else if (billing === 'annual') { anC += ext; anK += extCost; }
      else { moC += ext; moK += extCost; }
    });
    autoRows.forEach(a => {
      if ((a.billing || 'monthly') === 'annual') { anC += a.volume * a.sell; anK += a.volume * a.cost; }
      else { moC += a.volume * a.sell; moK += a.volume * a.cost; }
    });

    // Annual fees bill once per year started, not prorated by the leftover
    // partial year — a 13-month term spans into a 2nd year so it triggers 2
    // annual charges, a 24-month term (exactly 2 years) also triggers 2.
    const annualOccurrences = Math.max(1, Math.ceil(term / 12));
    const mrrC = moC * term, mrrK = moK * term;
    const arrC = anC * annualOccurrences, arrK = anK * annualOccurrences;
    const grandC = otC + mrrC + arrC, grandK = otK + mrrK + arrK;

    return {
      custTotal: moC, costTotal: moK,
      otCust: otC, otCost: otK,
      mrrCust: mrrC, mrrCost: mrrK,
      arrCust: arrC, arrCost: arrK,
      grandCust: grandC, grandCost: grandK,
      blendMargin: grandC > 0 ? (grandC - grandK) / grandC : 0,
    };
  }, [lines, autoRows, stdDisc, myo.term]);

  // ─── Persistence ─────────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      let raw = localStorage.getItem(LS_KEY);
      if (!raw) raw = localStorage.getItem('twilio_quote_builder_v1');
      const d = JSON.parse(raw || 'null');
      if (d) {
        if (d.lines) setLines(d.lines);
        if (d.meta) setMeta(m => ({ ...m, ...d.meta }));
        if (d.notesVal != null) setNotesVal(d.notesVal);
        if (d.autoFees != null) setAutoFees(d.autoFees);
        if (d.carrierRates) setCarrierRates(r => ({ ...r, ...d.carrierRates }));
        if (d.carrierBilling) setCarrierBilling(b => ({ ...b, ...d.carrierBilling }));
        if (d.myo) setMyo(m => ({ ...m, ...d.myo }));
        if (d.uid) nextId.current = d.uid;
        if (d.stdDisc) setStdDisc(d.stdDisc);
        if (d.hiddenCols) setHiddenCols(new Set(d.hiddenCols));
        if (d.dismissedCompanions) setDismissedCompanions(new Set(d.dismissedCompanions));
      }
    } catch (e) { /* ignore corrupt storage */ }
    setRestored(true);
  }, []);

  useEffect(() => {
    if (!restored) return;
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ lines, meta, notesVal, autoFees, carrierRates, carrierBilling, myo, stdDisc, hiddenCols: [...hiddenCols], dismissedCompanions: [...dismissedCompanions], uid: nextId.current }));
    } catch (e) { /* ignore quota errors */ }
  }, [lines, meta, notesVal, autoFees, carrierRates, carrierBilling, myo, stdDisc, hiddenCols, dismissedCompanions, restored]);

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handle = e => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // '/' or Cmd+K focuses search
  useEffect(() => {
    const handle = e => {
      if ((e.key === '/' || ((e.metaKey || e.ctrlKey) && e.key === 'k')) && document.activeElement !== searchInputRef.current) {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, []);

  // ─── Companion rules ──────────────────────────────────────────────────────────
  // `dismissed` holds companion names the user explicitly removed via the trash icon —
  // re-running this function (e.g. after adding another line) must not resurrect them.
  function applyCompanionRules(newLines, dismissed = dismissedCompanions) {
    let result = [...newLines];
    AUTO_COMPANION_RULES.forEach(rule => {
      const triggered = result.some(l => !l.companion && rule.trigger(l));
      if (triggered) {
        rule.companions.forEach(cName => {
          if (dismissed.has(cName)) return;
          if (!result.some(l => l.name === cName && l.companion)) {
            const cat = CATALOG.find(p => p.name === cName);
            if (cat) {
              result.push({ id: nextId.current++, category: cat.category, name: cat.name, volume: 1, list: cat.sell, sell: cat.sell, costRef: cat.cost, adjCost: cat.cost, companion: true, billing: defaultBillingFor(cat.name) });
            }
          }
        });
      } else {
        rule.companions.forEach(cName => {
          result = result.filter(l => !(l.name === cName && l.companion));
        });
      }
    });
    return result;
  }

  // ─── Line operations ──────────────────────────────────────────────────────────
  function addProduct(p) {
    const newLine = { id: nextId.current++, category: p.category, name: p.name, volume: 1, list: p.sell, sell: p.sell, costRef: p.cost, adjCost: p.cost, companion: false, billing: defaultBillingFor(p.name) };
    setLines(prev => applyCompanionRules([...prev, newLine]));
    setSearchQuery('');
    setSearchOpen(false);
    setActiveIdx(0);
  }

  function addTemplate(t) {
    setLines(prev => {
      let result = [...prev];
      t.items.forEach(item => {
        const cat = CATALOG.find(p => p.name === item.name);
        if (cat) result.push({ id: nextId.current++, category: cat.category, name: cat.name, volume: item.volume || 1, list: cat.sell, sell: cat.sell, costRef: cat.cost, adjCost: cat.cost, companion: false, billing: defaultBillingFor(cat.name) });
      });
      return applyCompanionRules(result);
    });
    setSearchQuery('');
    setSearchOpen(false);
    setActiveIdx(0);
  }

  function openCustomModal() {
    setCustomForm({ name: '', category: 'CUSTOM / MANUAL', volume: 1, list: 0, sell: 0, cost: 0, billing: 'monthly' });
    setCustomModalOpen(true);
    setSearchOpen(false);
  }

  function addCustomLine() {
    const name = customForm.name.trim();
    if (!name) return;
    const sell = +customForm.sell || 0;
    const list = +customForm.list || sell;
    const cost = +customForm.cost || 0;
    const newLine = {
      id: nextId.current++,
      category: customForm.category.trim() || 'CUSTOM / MANUAL',
      name,
      volume: Math.max(0, +customForm.volume || 0),
      list,
      sell,
      costRef: cost,
      adjCost: cost,
      companion: false,
      billing: customForm.billing || 'monthly',
    };
    setLines(prev => applyCompanionRules([...prev, newLine]));
    setCustomModalOpen(false);
    setSearchQuery('');
  }

  function removeLine(id) {
    setLines(prev => {
      const removed = prev.find(l => l.id === id);
      const filtered = prev.filter(l => l.id !== id);
      if (removed?.companion) {
        const nextDismissed = new Set(dismissedCompanions).add(removed.name);
        setDismissedCompanions(nextDismissed);
        return applyCompanionRules(filtered, nextDismissed);
      }
      return applyCompanionRules(filtered);
    });
  }

  function duplicateLine(id) {
    setLines(prev => {
      const idx = prev.findIndex(l => l.id === id);
      if (idx === -1) return prev;
      const clone = { ...prev[idx], id: nextId.current++, companion: false };
      const next = [...prev];
      next.splice(idx + 1, 0, clone);
      return applyCompanionRules(next);
    });
  }

  function updateLine(id, field, rawValue) {
    if (field === 'billing') {
      setLines(prev => prev.map(l => (l.id === id ? { ...l, billing: rawValue } : l)));
      return;
    }
    const v = parseFloat(rawValue) || 0;
    setLines(prev => prev.map(l => {
      if (l.id !== id) return l;
      if (field === 'volume') return { ...l, volume: Math.max(0, v) };
      if (field === 'sell') return { ...l, sell: v };
      if (field === 'disc') return { ...l, sell: +(l.list * (1 - v / 100)).toFixed(6) };
      if (field === 'adjCost') return { ...l, adjCost: v };
      return l;
    }));
  }

  // ─── MyOrders summary ─────────────────────────────────────────────────────────
  function myOrdersSegments() {
    const term = Math.max(1, +myo.term || 12);
    const sku = myo.sku || 'TWILIO-SAAS';

    const monthlyRows = [...lines.filter(l => (l.billing || 'monthly') === 'monthly'), ...autoRows.filter(a => (a.billing || 'monthly') === 'monthly')];
    const annualRows = [...lines.filter(l => l.billing === 'annual'), ...autoRows.filter(a => a.billing === 'annual')];
    const onetimeRows = lines.filter(l => l.billing === 'onetime');

    const sumRows = rows => rows.reduce((acc, r) => {
      const eSell = r.auto ? r.sell : getEffectiveSell(r, stdDisc);
      const cost = r.auto ? r.cost : r.adjCost;
      const vol = +r.volume || 0;
      acc.cust += vol * eSell;
      acc.cost += vol * (+cost || 0);
      return acc;
    }, { cust: 0, cost: 0 });

    const annualTotals = sumRows(annualRows);
    const onetimeTotals = sumRows(onetimeRows);

    const rowLine = r => {
      const eSell = r.auto ? r.sell : getEffectiveSell(r, stdDisc);
      return { t: `${r.name}: Qty. ${(+(r.volume || 0)).toLocaleString('en-US')} at ${fmtMoney(eSell, true)} per Unit` };
    };

    const twilioNote = { t: 'NOTE: Any Twilio products or services consumed that are not listed above will be charged at Twilio published pricing. For reference, please visit: https://www.twilio.com/pricing' };

    const noteBlock = (label, rows) => rows.length === 0 ? [] : [
      { t: 'Comment:' },
      { t: `The Unit Price is based on the following ${label} items and amounts:` },
      { t: '' },
      ...rows.map(rowLine),
      { t: '' },
    ];

    let lineNum = 0;
    const lineHeader = () => { lineNum += 1; return [{ h: true, t: `Line ${lineNum}` }, { t: `MyOrders SKU: ${sku}` }]; };

    const monthlyBlock = [
      ...lineHeader(),
      { h: true, t: 'Monthly Recurring:' },
      { t: `Customer Unit Price: ${fmtMoney(custTotal)} /mo` },
      { internal: true, t: `Presidio Unit Cost: ${fmtMoney(costTotal)} /mo` },
      { t: `Term for Extended Price: ${term} ${term === 1 ? 'month' : 'months'}` },
      { t: `Customer Extended Price: ${fmtMoney(custTotal * term)}` },
      { internal: true, t: `Presidio Extended Cost: ${fmtMoney(costTotal * term)}` },
      { t: '' },
      ...noteBlock('monthly recurring', monthlyRows),
      twilioNote,
      { t: '' },
    ];

    const annualBlock = annualRows.length === 0 ? [] : [
      ...lineHeader(),
      { h: true, t: 'Annual Recurring:' },
      { t: `Customer Unit Price: ${fmtMoney(annualTotals.cust)} /yr` },
      { internal: true, t: `Presidio Unit Cost: ${fmtMoney(annualTotals.cost)} /yr` },
      { t: '' },
      ...noteBlock('annual', annualRows),
      twilioNote,
      { t: '' },
    ];

    const onetimeBlock = onetimeRows.length === 0 ? [] : [
      ...lineHeader(),
      { h: true, t: 'One-Time:' },
      { t: `Customer Unit Price: ${fmtMoney(onetimeTotals.cust)} one-time` },
      { internal: true, t: `Presidio Unit Cost: ${fmtMoney(onetimeTotals.cost)} one-time` },
      { t: '' },
      ...noteBlock('one-time', onetimeRows),
      twilioNote,
    ];

    return [
      { t: `MyOrders Quote Summary — ${meta.cust || 'Customer'} - ${term} ${term === 1 ? 'month' : 'months'}` },
      { t: `Twilio SID: ${meta.sid || '—'}   Presidio SO#: ${meta.so || '—'}` },
      { t: '' },
      { h: true, t: 'Top-Level Quote Notes:' },
      { t: notesVal.trim() },
      { t: '' },

      ...monthlyBlock,
      ...annualBlock,
      ...onetimeBlock,
    ];
  }

  function myOrdersText() {
    return myOrdersSegments()
      .filter(s => !(view === 'customer' && s.internal))
      .map(s => s.t)
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  // ─── Export ───────────────────────────────────────────────────────────────────
  function exportCsv() {
    const billingLabel = b => b === 'onetime' ? 'One-Time' : b === 'annual' ? 'Annual' : 'Monthly';
    const toRow = l => { const eSell = getEffectiveSell(l, stdDisc); return { name: l.name, category: l.category, billing: billingLabel(l.billing), volume: +l.volume || 0, list: l.list, sell: eSell, ext: (+l.volume || 0) * eSell, cost: l.costRef, adjCost: l.adjCost, extCost: (+l.volume || 0) * (+l.adjCost || 0), margin: eSell > 0 ? (eSell - l.adjCost) / eSell : 0 }; };
    const autoRow = a => ({ name: a.name, category: 'Carrier fees (auto)', billing: billingLabel(a.billing), volume: a.volume, list: a.sell, sell: a.sell, ext: a.volume * a.sell, cost: a.cost, adjCost: a.cost, extCost: a.volume * a.cost, margin: 0 });

    const monthlyRows = [...lines.filter(l => (l.billing || 'monthly') === 'monthly').map(toRow), ...autoRows.filter(a => (a.billing || 'monthly') === 'monthly').map(autoRow)];
    const annualRows = [...lines.filter(l => l.billing === 'annual').map(toRow), ...autoRows.filter(a => a.billing === 'annual').map(autoRow)];
    const onetimeRows = lines.filter(l => l.billing === 'onetime').map(toRow);

    const esc = s => '"' + String(s).replace(/"/g, '""') + '"';
    const head = ['Product', 'Category', 'Frequency', 'Volume', 'Cust List/unit', 'Cust Sell/unit', 'Cust Ext', 'Presidio Cost/unit', 'Adj Cost/unit', 'Presidio Ext Cost', 'Margin %'];
    const rowCsv = r => [esc(r.name), esc(r.category), esc(r.billing), r.volume, r.list, r.sell, r.ext.toFixed(2), r.cost, r.adjCost, r.extCost.toFixed(2), (r.margin * 100).toFixed(1)].join(',');
    const subtotalCsv = (label, rows) => {
      const ext = rows.reduce((s, r) => s + r.ext, 0);
      const extCost = rows.reduce((s, r) => s + r.extCost, 0);
      return [esc(label), '', '', '', '', '', ext.toFixed(2), '', '', extCost.toFixed(2), ext > 0 ? ((ext - extCost) / ext * 100).toFixed(1) : '0'].join(',');
    };

    const body = [];
    const groups = [
      ['MONTHLY RECURRING', 'SUBTOTAL - MONTHLY', monthlyRows],
      ['ANNUAL RECURRING', 'SUBTOTAL - ANNUAL', annualRows],
      ['ONE-TIME', 'SUBTOTAL - ONE-TIME', onetimeRows],
    ];
    let allRows = [];
    groups.forEach(([groupLabel, subtotalLabel, rows]) => {
      if (rows.length === 0) return;
      if (body.length > 0) body.push('');
      body.push(esc(groupLabel));
      rows.forEach(r => body.push(rowCsv(r)));
      body.push(subtotalCsv(subtotalLabel, rows));
      allRows = allRows.concat(rows);
    });

    let cust = 0, cost = 0;
    allRows.forEach(r => { cust += r.ext; cost += r.extCost; });
    body.push('', [esc('TOTALS'), '', '', '', '', '', cust.toFixed(2), '', '', cost.toFixed(2), cust > 0 ? ((cust - cost) / cust * 100).toFixed(1) : '0'].join(','));
    const pre = [`Customer,${esc(meta.cust)}`, `Twilio SID,${esc(meta.sid)}`, `Presidio SO#,${esc(meta.so)}`, ''].join('\n');
    const csv = pre + '\n' + head.map(esc).join(',') + '\n' + body.join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `Twilio_Quote_${(meta.cust || 'Customer').replace(/[^a-z0-9]+/gi, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function copySummary() {
    navigator.clipboard.writeText(myOrdersText()).catch(() => {});
  }

  // ─── Search keyboard navigation ───────────────────────────────────────────────
  function handleSearchKeyDown(e) {
    if (!searchOpen || !searchResults.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, searchResults.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (searchResults[activeIdx]) addProduct(searchResults[activeIdx]); }
    else if (e.key === 'Escape') { setSearchOpen(false); searchInputRef.current?.blur(); }
  }

  // Highlight matching search terms in product name (safe — text comes from CATALOG constant)
  function highlightText(text) {
    const terms = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return text;
    const pattern = terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    const regex = new RegExp(`(${pattern})`, 'gi');
    const parts = [];
    let last = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > last) parts.push(text.slice(last, match.index));
      parts.push(<mark key={match.index} style={{ background: '#FCE6B4', borderRadius: 2, padding: '0 1px', fontStyle: 'normal' }}>{match[0]}</mark>);
      last = regex.lastIndex;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
  }

  // ─── Render ───────────────────────────────────────────────────────────────────
  const totalCount = lines.length + autoRows.length;
  const isInternal = view === 'internal';
  const visibleCols = COLS.filter(c => {
    if (c.internal && !isInternal) return false;
    if (!c.always && hiddenCols.has(c.key)) return false;
    return true;
  });
  const showCol = key => visibleCols.some(c => c.key === key);
  const colSpan = visibleCols.length;
  const segments = myOrdersSegments();

  return (
    <Box style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--mantine-color-gray-0)', paddingBottom: 100 }}>
      <PlatformNavBar
        onBackToPlatformSelector={onBackToPlatformSelector}
        onBackToHome={onBackToHome}
        showHome={!!onBackToHome}
        homeLabel="SA Tools"
        color="purple"
      />

      <Container size={1600} p="lg" style={{ flex: 1 }}>
        <Stack gap="md">

          {/* Page header + view toggle */}
          <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
            <Box>
              <Title order={2} c="purple">Twilio Quote Builder</Title>
              <Text size="sm" c="dimmed">Presidio · SaaS Pricing</Text>
            </Box>
            <Group gap="sm">
              <SegmentedControl
                value={view}
                onChange={setView}
                data={[{ value: 'internal', label: 'Internal' }, { value: 'customer', label: 'Customer' }]}
                color="purple"
                size="sm"
              />
              <Button variant="light" color="purple" size="sm" leftSection={<IconDownload size={15} />} onClick={exportCsv}>
                CSV
              </Button>
              <Button
                variant="light" color="gray" size="sm"
                onClick={() => {
                  if (lines.length === 0 || window.confirm('Start a new quote? This clears the current line items.')) {
                    setLines([]);
                    setMeta({ cust: '', sid: '', so: '' });
                    setDismissedCompanions(new Set());
                  }
                }}
              >
                New
              </Button>
            </Group>
          </Group>

          {/* Meta fields: Customer / SID / SO# */}
          <Grid>
            {[
              { key: 'cust', label: 'Customer name', placeholder: 'Acme Corporation', mono: false },
              { key: 'sid', label: 'Twilio SID', placeholder: 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx', mono: true },
              { key: 'so', label: 'Presidio SO #', placeholder: 'SO-000000', mono: true },
            ].map(f => (
              <Grid.Col key={f.key} span={{ base: 12, sm: 4 }}>
                <Paper withBorder p="sm" shadow="xs">
                  <TextInput
                    label={f.label}
                    placeholder={f.placeholder}
                    value={meta[f.key]}
                    onChange={e => setMeta(m => ({ ...m, [f.key]: e.target.value }))}
                    variant="unstyled"
                    size="md"
                    fw={550}
                    styles={{
                      label: { fontSize: 10.5, fontWeight: 650, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--mantine-color-dimmed)' },
                      input: f.mono ? { fontFamily: 'ui-monospace, monospace', fontSize: 13 } : undefined,
                    }}
                  />
                </Paper>
              </Grid.Col>
            ))}
          </Grid>

          {/* Search bar */}
          <Box ref={searchContainerRef} style={{ position: 'relative' }}>
            <TextInput
              ref={searchInputRef}
              size="lg"
              placeholder={`Search ${CATALOG.length} Twilio products to add to this quote…`}
              leftSection={<IconSearch size={19} />}
              rightSection={
                searchQuery
                  ? <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => { setSearchQuery(''); setSearchOpen(false); }}>
                      <IconX size={14} />
                    </ActionIcon>
                  : <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>↑↓ · ↵</Text>
              }
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setActiveIdx(0); setSearchOpen(true); }}
              onFocus={() => setSearchOpen(true)}
              onKeyDown={handleSearchKeyDown}
              styles={{ input: { borderWidth: 1.5 } }}
            />

            {searchOpen && (
              <Paper
                shadow="xl" withBorder
                style={{ position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 30, maxHeight: 420, overflowY: 'auto', marginTop: 4 }}
              >
                {/* Custom line item */}
                <Box>
                  <Box
                    px="sm" py={9}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', borderRadius: 6 }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--mantine-color-teal-0)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    onMouseDown={e => { e.preventDefault(); openCustomModal(); }}
                  >
                    <IconPlus size={15} color="var(--mantine-color-teal-6)" style={{ flexShrink: 0 }} />
                    <Box style={{ flex: 1 }}>
                      <Text size="sm" fw={500}>Add Custom Line Item</Text>
                      <Text size="xs" c="dimmed">Manually enter a product not in the catalog</Text>
                    </Box>
                    <Badge size="xs" variant="light" color="teal">custom</Badge>
                  </Box>
                </Box>

                {/* Templates section */}
                {templateResults.length > 0 && (
                  <Box>
                    <Text size="xs" fw={700} tt="uppercase" lts="0.07em" c="dimmed"
                      px="sm" pt="xs" pb={4}
                      style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}
                    >
                      Templates
                    </Text>
                    {templateResults.map(t => (
                      <Box
                        key={t.name} px="sm" py={9}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', borderRadius: 6 }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--mantine-color-violet-0)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        onMouseDown={e => { e.preventDefault(); addTemplate(t); }}
                      >
                        <IconTemplate size={15} color="var(--mantine-color-violet-6)" style={{ flexShrink: 0 }} />
                        <Box style={{ flex: 1 }}>
                          <Text size="sm" fw={500}>{t.name}</Text>
                          <Text size="xs" c="dimmed">{t.description}</Text>
                        </Box>
                        <Badge size="xs" variant="light" color="violet">template</Badge>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Product results */}
                {searchQuery.trim() && (searchResults.length === 0 ? (
                  <Text p="xl" ta="center" c="dimmed" size="sm">No products match that search.</Text>
                ) : (
                  groupedResults.map(({ category, items }) => (
                    <Box key={category}>
                      <Text
                        size="xs" fw={700} tt="uppercase" lts="0.07em" c="dimmed"
                        px="sm" pt="xs" pb={4}
                        style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}
                      >
                        {category}
                      </Text>
                      {items.map(p => {
                        const globalIdx = searchResults.indexOf(p);
                        const isActive = globalIdx === activeIdx;
                        return (
                          <Box
                            key={p.name}
                            px="sm" py={9}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', borderRadius: 6,
                              background: isActive ? 'var(--mantine-color-red-0)' : 'transparent',
                            }}
                            onMouseMove={() => setActiveIdx(globalIdx)}
                            onMouseDown={e => { e.preventDefault(); addProduct(p); }}
                          >
                            <Text size="sm" style={{ flex: 1 }}>{highlightText(p.name)}</Text>
                            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                              list <Text component="span" fw={600} c="dark">{fmtMoney(p.sell, true)}</Text>
                              {' · '}cost {fmtMoney(p.cost, true)}
                            </Text>
                            <Badge
                              size="xs" variant="light" color="red"
                              style={{ opacity: isActive ? 1 : 0, transition: 'opacity 0.1s', flexShrink: 0 }}
                            >
                              Add +
                            </Badge>
                          </Box>
                        );
                      })}
                    </Box>
                  ))
                ))}
              </Paper>
            )}
          </Box>

          {/* Custom line item modal */}
          <Modal
            opened={customModalOpen}
            onClose={() => setCustomModalOpen(false)}
            title="Add Custom Line Item"
            centered
          >
            <Stack gap="sm">
              <TextInput
                label="Product name" placeholder="e.g. Custom Integration Fee" required
                value={customForm.name}
                onChange={e => setCustomForm(f => ({ ...f, name: e.target.value }))}
                data-autofocus
              />
              <TextInput
                label="Category" placeholder="CUSTOM / MANUAL"
                value={customForm.category}
                onChange={e => setCustomForm(f => ({ ...f, category: e.target.value }))}
              />
              <Box>
                <Text size="xs" fw={650} tt="uppercase" lts="0.06em" c="dimmed" mb={4}>Frequency</Text>
                <SegmentedControl
                  fullWidth
                  value={customForm.billing}
                  onChange={v => setCustomForm(f => ({ ...f, billing: v }))}
                  data={[{ label: 'One-Time', value: 'onetime' }, { label: 'Monthly', value: 'monthly' }, { label: 'Annual', value: 'annual' }]}
                />
              </Box>
              <Grid>
                <Grid.Col span={6}>
                  <NumberInput
                    label="Volume" min={0} step={1} hideControls
                    value={customForm.volume}
                    onChange={v => setCustomForm(f => ({ ...f, volume: Math.max(0, +v || 0) }))}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <NumberInput
                    label="Cust list $/unit" min={0} step={0.01} decimalScale={6} hideControls
                    value={customForm.list}
                    onChange={v => setCustomForm(f => ({ ...f, list: +v || 0 }))}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <NumberInput
                    label="Cust sell $/unit" min={0} step={0.01} decimalScale={6} hideControls
                    value={customForm.sell}
                    onChange={v => setCustomForm(f => ({ ...f, sell: +v || 0 }))}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <NumberInput
                    label="Presidio cost $/unit" min={0} step={0.01} decimalScale={6} hideControls
                    value={customForm.cost}
                    onChange={v => setCustomForm(f => ({ ...f, cost: +v || 0 }))}
                  />
                </Grid.Col>
              </Grid>
              <Group justify="flex-end" mt="sm">
                <Button variant="subtle" color="gray" onClick={() => setCustomModalOpen(false)}>Cancel</Button>
                <Button color="teal" leftSection={<IconPlus size={15} />} disabled={!customForm.name.trim()} onClick={addCustomLine}>
                  Add to quote
                </Button>
              </Group>
            </Stack>
          </Modal>

          {/* Line items table */}
          <Paper withBorder shadow="xs" style={{ overflow: 'hidden' }}>
            <Group px="md" py="sm" justify="space-between" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
              <Group gap="sm">
                <Text size="sm" fw={700} tt="uppercase" lts="0.02em" c="dimmed">Quote line items</Text>
                <Badge variant="outline" color="gray" size="sm">
                  {totalCount} item{totalCount !== 1 ? 's' : ''}
                </Badge>
              </Group>
              {isInternal && (
                <Group gap="lg">
                  <Switch
                    checked={autoFees} onChange={e => setAutoFees(e.currentTarget.checked)}
                    label="Auto carrier fees" size="sm" color="purple"
                  />
                  <Group gap="xs" align="center">
                    <Switch
                      checked={stdDisc.enabled}
                      onChange={e => {
                        const enabling = e.currentTarget.checked;
                        if (!enabling) {
                          setLines(prev => prev.map(l => ({ ...l, sell: getEffectiveSell(l, stdDisc) })));
                        }
                        setStdDisc(d => ({ ...d, enabled: enabling }));
                      }}
                      label="Standard discount" size="sm" color="purple"
                    />
                    <NumberInput
                      value={stdDisc.pct}
                      onChange={v => setStdDisc(d => ({ ...d, pct: Math.min(100, Math.max(0, +v || 0)) }))}
                      min={0} max={100} step={1} hideControls
                      disabled={!stdDisc.enabled}
                      w={62} size="xs"
                      rightSection={<Text size="xs" c="dimmed">%</Text>}
                      styles={{ input: { textAlign: 'right' } }}
                    />
                  </Group>
                </Group>
              )}
            </Group>

            <Box style={{ overflowX: 'auto' }}>
              <Table style={{ minWidth: 800 }} highlightOnHover striped={false}>
                <Table.Thead style={{ background: '#FCFCFD' }}>
                  <Table.Tr>
                    {visibleCols.map(col => (
                      <Table.Th
                        key={col.key}
                        style={{
                          textAlign: col.align, fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: '0.05em', color: 'var(--mantine-color-dimmed)',
                          padding: '9px 12px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--mantine-color-gray-2)',
                        }}
                      >
                        {col.key === 'actions' ? (
                          <Popover position="bottom-end" withArrow shadow="md">
                            <Popover.Target>
                              <ActionIcon variant="subtle" color="gray" size="sm">
                                <IconColumns size={15} />
                              </ActionIcon>
                            </Popover.Target>
                            <Popover.Dropdown p="xs">
                              <Text size="xs" fw={700} tt="uppercase" lts="0.06em" c="dimmed" mb="xs">Show / hide columns</Text>
                              <Stack gap={6}>
                                {COLS.filter(c => !c.always && (!c.internal || isInternal)).map(c => (
                                  <Checkbox
                                    key={c.key}
                                    label={c.label + (c.sub ? ` (${c.sub})` : '')}
                                    size="xs"
                                    checked={!hiddenCols.has(c.key)}
                                    onChange={() => setHiddenCols(prev => {
                                      const next = new Set(prev);
                                      if (next.has(c.key)) next.delete(c.key); else next.add(c.key);
                                      return next;
                                    })}
                                  />
                                ))}
                              </Stack>
                            </Popover.Dropdown>
                          </Popover>
                        ) : col.key === 'billing' ? (
                          <Group gap={3} justify="center" wrap="nowrap">
                            <Text size="inherit" fw="inherit" tt="inherit" lts="inherit" c="inherit">{col.label}</Text>
                            <Tooltip label="O = One-Time · M = Monthly · A = Annual" withArrow multiline w={200}>
                              <Text size="9px" fw={700} c="dimmed" style={{ cursor: 'help', border: '1px solid currentColor', borderRadius: '50%', width: 12, height: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>?</Text>
                            </Tooltip>
                          </Group>
                        ) : (
                          <>
                            {col.label}
                            {col.sub && <Box component="span" display="block" style={{ fontWeight: 500, fontSize: 9, letterSpacing: '0.04em', textTransform: 'none', color: '#A4ABB7', marginTop: 1 }}>{col.sub}</Box>}
                          </>
                        )}
                      </Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {totalCount === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={colSpan}>
                        <Box py="xl" ta="center">
                          <Text fw={600} c="dimmed" mb={4}>No products yet</Text>
                          <Text size="sm" c="dimmed">Search above and press Enter, or click a result, to build the quote.</Text>
                        </Box>
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    <>
                      {lines.map(l => {
                        const eSell = getEffectiveSell(l, stdDisc);
                        const ext = (+l.volume || 0) * eSell;
                        const extCost = (+l.volume || 0) * (+l.adjCost || 0);
                        const m = eSell > 0 ? (eSell - l.adjCost) / eSell : 0;
                        const discPct = l.list > 0 ? (1 - eSell / l.list) * 100 : 0;
                        return (
                          <Table.Tr key={l.id} style={{ fontSize: 13 }}>
                            <Table.Td style={{ textAlign: 'left', padding: '7px 12px' }}>
                              <Text size="sm" fw={550} lh={1.3}>{l.name}</Text>
                              <Group gap={4} mt={2}>
                                <Text size="xs" c="dimmed">{l.category}</Text>
                              </Group>
                            </Table.Td>
                            {showCol('billing') && <Table.Td style={{ textAlign: 'center', padding: '7px 8px' }}>
                              <SegmentedControl
                                value={l.billing || 'monthly'}
                                onChange={v => updateLine(l.id, 'billing', v)}
                                data={[{ label: 'O', value: 'onetime' }, { label: 'M', value: 'monthly' }, { label: 'A', value: 'annual' }]}
                                size="xs"
                                color="purple"
                              />
                            </Table.Td>}
                            {showCol('volume') && <Table.Td style={{ textAlign: 'right', padding: '7px 12px' }}>
                              <input
                                type="text" inputMode="numeric"
                                defaultValue={Number(l.volume).toLocaleString('en-US')}
                                key={`vol-${l.id}`}
                                onChange={e => updateLine(l.id, 'volume', e.target.value.replace(/,/g, ''))}
                                onFocus={e => { e.target.value = e.target.value.replace(/,/g, ''); e.target.select(); e.target.style.borderColor = 'var(--mantine-color-purple-6)'; e.target.style.background = 'white'; }}
                                onBlur={e => { const n = Math.max(0, parseInt(e.target.value.replace(/,/g, ''), 10) || 0); e.target.value = n.toLocaleString('en-US'); e.target.style.borderColor = 'transparent'; e.target.style.background = 'transparent'; }}
                                onMouseEnter={e => { if (document.activeElement !== e.target) e.target.style.borderColor = 'var(--mantine-color-gray-4)'; }}
                                onMouseLeave={e => { if (document.activeElement !== e.target) e.target.style.borderColor = 'transparent'; }}
                                style={{ ...cellInput, width: 110, fontWeight: 600 }}
                              />
                            </Table.Td>}
                            {showCol('list') && <Table.Td style={{ textAlign: 'right', padding: '7px 12px', color: 'var(--mantine-color-dimmed)', fontVariantNumeric: 'tabular-nums' }}>
                              {fmtMoney(l.list, true)}
                            </Table.Td>}
                            {showCol('disc') && <Table.Td style={{ textAlign: 'right', padding: '7px 12px' }}>
                              <Group gap={2} justify="flex-end" wrap="nowrap">
                                <input
                                  type="number" step={1}
                                  defaultValue={stdDisc.enabled ? stdDisc.pct : +discPct.toFixed(2)}
                                  key={`disc-${l.id}-${stdDisc.enabled}-${stdDisc.pct}`}
                                  disabled={stdDisc.enabled}
                                  onChange={e => updateLine(l.id, 'disc', e.target.value)}
                                  onFocus={e => { e.target.select(); e.target.style.borderColor = 'var(--mantine-color-purple-6)'; e.target.style.background = 'white'; }}
                                  onBlur={e => { e.target.style.borderColor = 'transparent'; e.target.style.background = 'transparent'; }}
                                  onMouseEnter={e => { if (document.activeElement !== e.target) e.target.style.borderColor = 'var(--mantine-color-gray-4)'; }}
                                  onMouseLeave={e => { if (document.activeElement !== e.target) e.target.style.borderColor = 'transparent'; }}
                                  style={{ ...cellInput, width: 52 }}
                                />
                                <Text size="xs" c="dimmed">%</Text>
                              </Group>
                            </Table.Td>}
                            {showCol('sell') && <Table.Td style={{ textAlign: 'right', padding: '7px 12px' }}>
                              <input
                                type="text" inputMode="decimal"
                                defaultValue={fmtSellInput(eSell)}
                                key={`sell-${l.id}-${stdDisc.enabled}-${stdDisc.pct}`}
                                disabled={stdDisc.enabled}
                                onChange={e => updateLine(l.id, 'sell', e.target.value)}
                                onFocus={e => { e.target.select(); e.target.style.borderColor = 'var(--mantine-color-purple-6)'; e.target.style.background = 'white'; }}
                                onBlur={e => { e.target.style.borderColor = 'transparent'; e.target.style.background = 'transparent'; }}
                                onMouseEnter={e => { if (document.activeElement !== e.target) e.target.style.borderColor = 'var(--mantine-color-gray-4)'; }}
                                onMouseLeave={e => { if (document.activeElement !== e.target) e.target.style.borderColor = 'transparent'; }}
                                style={{ ...cellInput, width: 88 }}
                              />
                            </Table.Td>}
                            {showCol('ext') && <Table.Td style={{ textAlign: 'right', padding: '7px 12px', fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>
                              {fmtMoney(ext)}
                            </Table.Td>}
                            {showCol('cost') && <Table.Td style={{ textAlign: 'right', padding: '7px 12px', color: 'var(--mantine-color-dimmed)', fontVariantNumeric: 'tabular-nums' }}>
                              {fmtCost(l.costRef)}
                            </Table.Td>}
                            {showCol('adjCost') && <Table.Td style={{ textAlign: 'right', padding: '7px 12px' }}>
                              <input
                                type="text" inputMode="decimal"
                                defaultValue={fmtCostInput(l.adjCost)}
                                key={`adj-${l.id}`}
                                onChange={e => updateLine(l.id, 'adjCost', e.target.value)}
                                onFocus={e => { e.target.select(); e.target.style.borderColor = 'var(--mantine-color-purple-6)'; e.target.style.background = 'white'; }}
                                onBlur={e => { e.target.style.borderColor = 'transparent'; e.target.style.background = 'transparent'; }}
                                onMouseEnter={e => { if (document.activeElement !== e.target) e.target.style.borderColor = 'var(--mantine-color-gray-4)'; }}
                                onMouseLeave={e => { if (document.activeElement !== e.target) e.target.style.borderColor = 'transparent'; }}
                                style={{ ...cellInput, width: 88 }}
                              />
                            </Table.Td>}
                            {showCol('extCost') && <Table.Td style={{ textAlign: 'right', padding: '7px 12px', fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>
                              {fmtMoney(extCost)}
                            </Table.Td>}
                            {showCol('margin') && <Table.Td style={{ textAlign: 'right', padding: '7px 12px' }}>
                              <Badge color={marginMantineColor(m)} variant="light" size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {(m * 100).toFixed(0)}%
                              </Badge>
                            </Table.Td>}
                            <Table.Td style={{ padding: '7px 12px' }}>
                              <Group gap={4} justify="flex-end" wrap="nowrap">
                                <Tooltip label="Duplicate" withArrow>
                                  <ActionIcon variant="subtle" color="blue" size="sm" onClick={() => duplicateLine(l.id)}>
                                    <IconCopyPlus size={15} />
                                  </ActionIcon>
                                </Tooltip>
                                <Tooltip label="Remove" withArrow>
                                  <ActionIcon variant="subtle" color="red" size="sm" onClick={() => removeLine(l.id)}>
                                    <IconTrash size={15} />
                                  </ActionIcon>
                                </Tooltip>
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}

                      {autoRows.map((a, i) => (
                        <Table.Tr key={`auto-${i}`} style={{ fontSize: 13, opacity: 0.85 }}>
                          <Table.Td style={{ textAlign: 'left', padding: '7px 12px' }}>
                            <Text size="sm" fw={500}>{a.name}</Text>
                            <Text size="xs" c="dimmed" mt={1}>Estimated messaging carrier fees</Text>
                          </Table.Td>
                          {showCol('billing') && <Table.Td style={{ textAlign: 'center', padding: '7px 8px' }}>
                            <SegmentedControl
                              value={a.billing || 'monthly'}
                              onChange={v => setCarrierBilling(prev => ({ ...prev, [a.key]: v }))}
                              data={[{ label: 'M', value: 'monthly' }, { label: 'A', value: 'annual' }]}
                              size="xs"
                              color="purple"
                            />
                          </Table.Td>}
                          {showCol('volume') && <Table.Td style={{ textAlign: 'right', padding: '7px 12px', fontVariantNumeric: 'tabular-nums' }}>{a.volume.toLocaleString('en-US')}</Table.Td>}
                          {showCol('list') && <Table.Td style={{ textAlign: 'right', padding: '7px 12px', color: 'var(--mantine-color-dimmed)', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(a.sell, true)}</Table.Td>}
                          {showCol('disc') && <Table.Td style={{ textAlign: 'right', padding: '7px 12px', color: 'var(--mantine-color-dimmed)' }}>—</Table.Td>}
                          {showCol('sell') && <Table.Td style={{ textAlign: 'right', padding: '7px 12px', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(a.sell, true)}</Table.Td>}
                          {showCol('ext') && <Table.Td style={{ textAlign: 'right', padding: '7px 12px', fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(a.volume * a.sell)}</Table.Td>}
                          {showCol('cost') && <Table.Td style={{ textAlign: 'right', padding: '7px 12px', color: 'var(--mantine-color-dimmed)', fontVariantNumeric: 'tabular-nums' }}>{fmtCost(a.cost)}</Table.Td>}
                          {showCol('adjCost') && <Table.Td style={{ textAlign: 'right', padding: '7px 12px', color: 'var(--mantine-color-dimmed)', fontVariantNumeric: 'tabular-nums' }}>{fmtCost(a.cost)}</Table.Td>}
                          {showCol('extCost') && <Table.Td style={{ textAlign: 'right', padding: '7px 12px', fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(a.volume * a.cost)}</Table.Td>}
                          {showCol('margin') && <Table.Td style={{ textAlign: 'right', padding: '7px 12px' }}>
                            <Badge color="yellow" variant="light" size="sm">0%</Badge>
                          </Table.Td>}
                          <Table.Td style={{ padding: '7px 12px' }} />
                        </Table.Tr>
                      ))}
                    </>
                  )}
                </Table.Tbody>
              </Table>
            </Box>

            {/* Carrier rate editor (internal only, visible when auto rows exist) */}
            {isInternal && autoFees && autoRows.length > 0 && (
              <Group gap="xl" px="md" py="sm" style={{ background: '#FBFCFE', borderTop: '1px solid var(--mantine-color-gray-2)' }}>
                <Text size="sm" fw={600} c="dimmed">Estimated carrier rates:</Text>
                <Group gap="xs" align="center">
                  <Text size="xs" c="dimmed">SMS / RCS / unit $</Text>
                  <NumberInput
                    value={carrierRates.sms}
                    onChange={v => setCarrierRates(r => ({ ...r, sms: +v || 0 }))}
                    step={0.001} decimalScale={4} hideControls
                    size="xs" w={78}
                    styles={{ input: { textAlign: 'right' } }}
                  />
                </Group>
                <Group gap="xs" align="center">
                  <Text size="xs" c="dimmed">MMS media / unit $</Text>
                  <NumberInput
                    value={carrierRates.mms}
                    onChange={v => setCarrierRates(r => ({ ...r, mms: +v || 0 }))}
                    step={0.001} decimalScale={4} hideControls
                    size="xs" w={78}
                    styles={{ input: { textAlign: 'right' } }}
                  />
                </Group>
              </Group>
            )}
          </Paper>

          {/* MyOrders Quote Summary */}
          <Paper withBorder shadow="xs" style={{ overflow: 'hidden' }}>
            <Group px="md" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
              <Text size="sm" fw={700} tt="uppercase" lts="0.02em" c="dimmed">MyOrders Quote Summary</Text>
              <Box style={{ flex: 1 }} />
              <Button size="xs" variant="outline" color="gray" leftSection={<IconCopy size={13} />} onClick={copySummary}>
                Copy summary
              </Button>
            </Group>

            <Stack p="md" gap="md">
              <Box>
                <Text size="xs" fw={650} tt="uppercase" lts="0.07em" c="dimmed" mb={5}>Top-level quote notes</Text>
                <Textarea value={notesVal} onChange={e => setNotesVal(e.target.value)} minRows={3} autosize />
              </Box>

              <Grid>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Paper withBorder p="sm">
                    <Text size="xs" fw={650} tt="uppercase" lts="0.06em" c="dimmed" mb={3}>MyOrders SKU</Text>
                    <TextInput
                      value={myo.sku} onChange={e => setMyo(m => ({ ...m, sku: e.target.value }))}
                      variant="unstyled" size="md" fw={600}
                      styles={{ input: { fontFamily: 'ui-monospace, monospace' } }}
                    />
                  </Paper>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Paper withBorder p="sm">
                    <Text size="xs" fw={650} tt="uppercase" lts="0.06em" c="dimmed" mb={3}>Term for extended price</Text>
                    <NumberInput
                      value={myo.term} onChange={v => setMyo(m => ({ ...m, term: Math.max(1, +v || 12) }))}
                      min={1} step={1} hideControls variant="unstyled" size="md" fw={600}
                    />
                  </Paper>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 3 }}>
                  <Paper withBorder p="sm" style={{ background: '#FCFCFD' }}>
                    <Text size="xs" fw={650} tt="uppercase" lts="0.06em" c="dimmed" mb={3}>One-Time</Text>
                    <Text size="lg" fw={700} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {fmtMoney(otCust)}
                    </Text>
                  </Paper>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 3 }}>
                  <Paper withBorder p="sm" style={{ background: '#FCFCFD' }}>
                    <Text size="xs" fw={650} tt="uppercase" lts="0.06em" c="dimmed" mb={3}>MRR <Text component="span" size="9px" c="dimmed">(mo. × term)</Text></Text>
                    <Text size="lg" fw={700} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {fmtMoney(mrrCust)}
                    </Text>
                  </Paper>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 3 }}>
                  <Paper withBorder p="sm" style={{ background: '#FCFCFD' }}>
                    <Text size="xs" fw={650} tt="uppercase" lts="0.06em" c="dimmed" mb={3}>ARR <Text component="span" size="9px" c="dimmed">(annual × yrs in term)</Text></Text>
                    <Text size="lg" fw={700} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {fmtMoney(arrCust)}
                    </Text>
                  </Paper>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 3 }}>
                  <Paper withBorder p="sm" style={{ background: 'var(--mantine-color-purple-0)' }}>
                    <Text size="xs" fw={650} tt="uppercase" lts="0.06em" c="dimmed" mb={3}>Total <Text component="span" size="9px" c="dimmed">(over term)</Text></Text>
                    <Text size="lg" fw={800} c="purple" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {fmtMoney(grandCust)}
                    </Text>
                  </Paper>
                </Grid.Col>
                {isInternal && (
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Paper withBorder p="sm" style={{ background: '#FCFCFD' }}>
                      <Text size="xs" fw={650} tt="uppercase" lts="0.06em" c="dimmed" mb={3}>Presidio unit cost (rate)</Text>
                      <Text size="lg" fw={700} c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmtMoney(costTotal)}<Text component="span" size="xs" fw={550} c="dimmed"> /mo</Text>
                      </Text>
                    </Paper>
                  </Grid.Col>
                )}
                {isInternal && (
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Paper withBorder p="sm" style={{ background: '#FCFCFD' }}>
                      <Text size="xs" fw={650} tt="uppercase" lts="0.06em" c="dimmed" mb={3}>Presidio total cost <Text component="span" size="9px" c="dimmed">(over term)</Text></Text>
                      <Text size="lg" fw={700} c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmtMoney(grandCost)}
                      </Text>
                    </Paper>
                  </Grid.Col>
                )}
              </Grid>

              {/* Generated output preview */}
              <Paper withBorder style={{ overflow: 'hidden' }}>
                <Group px="sm" py="xs" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', background: 'white' }}>
                  <Text size="xs" fw={700} tt="uppercase" lts="0.06em" c="dimmed">Generated output</Text>
                  <Text size="xs" c="dimmed" style={{ flex: 1 }}>updates live from line items</Text>
                </Group>
                <Box
                  p="md"
                  style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12.5, lineHeight: 1.6, maxHeight: 420, overflowY: 'auto', background: '#FCFCFD', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {segments
                    .filter(s => !(view === 'customer' && s.internal))
                    .map((s, i) => (
                      <Box
                        key={i}
                        component="span"
                        display="block"
                        style={{ fontWeight: s.h ? 700 : 400, color: s.h ? 'var(--mantine-color-dark-9)' : 'var(--mantine-color-dimmed)' }}
                      >
                        {s.t || '​'}
                      </Box>
                    ))}
                </Box>
              </Paper>
            </Stack>
          </Paper>

        </Stack>
      </Container>

      <Footer />

      {/* Fixed bottom dock — totals + primary actions */}
      <Box
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 35,
          background: 'white',
          borderTop: '1px solid var(--mantine-color-gray-3)',
          boxShadow: '0 -8px 24px rgba(16,24,40,.08)',
        }}
      >
        <Container size={1600}>
          <Group py="sm" gap="xl" wrap="nowrap" align="center">
            <Box>
              <Text size="xs" fw={650} tt="uppercase" lts="0.06em" c="dimmed">One-Time</Text>
              <Text size="lg" fw={700} lh={1.2} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmtMoney(otCust)}
              </Text>
            </Box>

            <Box>
              <Text size="xs" fw={650} tt="uppercase" lts="0.06em" c="dimmed">MRR <Text component="span" size="9px" c="dimmed">(mo. × term)</Text></Text>
              <Text size="lg" fw={700} lh={1.2} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmtMoney(mrrCust)}
              </Text>
            </Box>

            <Box>
              <Text size="xs" fw={650} tt="uppercase" lts="0.06em" c="dimmed">ARR <Text component="span" size="9px" c="dimmed">(annual × yrs in term)</Text></Text>
              <Text size="lg" fw={700} lh={1.2} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmtMoney(arrCust)}
              </Text>
            </Box>

            <Box>
              <Text size="xs" fw={650} tt="uppercase" lts="0.06em" c="dimmed">Total <Text component="span" size="9px" c="dimmed">(over term)</Text></Text>
              <Text size="xl" fw={800} lh={1.2} c="purple" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmtMoney(grandCust)}
              </Text>
            </Box>

            {isInternal && (
              <>
                <Box>
                  <Text size="xs" fw={650} tt="uppercase" lts="0.06em" c="dimmed">Presidio total cost <Text component="span" size="9px" c="dimmed">(over term)</Text></Text>
                  <Text size="lg" fw={700} lh={1.2} c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {fmtMoney(grandCost)}
                  </Text>
                </Box>
                <Box style={{ flex: 1, minWidth: 140 }}>
                  <Group gap={6} mb={6} align="center">
                    <Text size="xs" fw={650} tt="uppercase" lts="0.06em" c="dimmed">Blended margin</Text>
                    <Text size="xs" fw={700} style={{ color: marginHex(blendMargin), fontVariantNumeric: 'tabular-nums' }}>
                      {(blendMargin * 100).toFixed(1)}%
                    </Text>
                  </Group>
                  <Box style={{ height: 9, borderRadius: 999, background: 'var(--mantine-color-gray-1)', overflow: 'hidden', border: '1px solid var(--mantine-color-gray-3)' }}>
                    <Box style={{ height: '100%', borderRadius: 999, width: `${Math.min(blendMargin * 100, 100)}%`, background: marginHex(blendMargin), transition: 'width 0.25s ease, background 0.25s' }} />
                  </Box>
                </Box>
              </>
            )}

            <Box style={{ flex: isInternal ? 0 : 1 }} />

            <Group gap="sm" wrap="nowrap">
              <Button variant="outline" color="gray" leftSection={<IconPrinter size={16} />} onClick={() => window.print()}>
                Print / PDF
              </Button>
              <Button color="purple" leftSection={<IconCopy size={16} />} onClick={copySummary}>
                Copy summary
              </Button>
            </Group>
          </Group>
        </Container>
      </Box>
    </Box>
  );
}
